import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, X } from "@phosphor-icons/react";
import { api } from "../../utils/api";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Button } from "../shared/Button";

const CATEGORIAS_LABEL = {
  pintura: "Pintura",
  suelos: "Suelos",
  revestimientos: "Revestimientos",
  "cerámica": "Cerámica",
  "tabiquería": "Tabiquería",
  "fontanería": "Fontanería",
  electricidad: "Electricidad",
  "carpintería": "Carpintería",
  metal: "Metal",
  "baño": "Baño",
  cocina: "Cocina",
  herrajes: "Herrajes",
  otros: "Otros",
};

// Catálogo de materiales de referencia — a propósito de solo lectura para el
// cliente (sin precio: Andy no vende materiales, instala) y sin upload acá
// (las fotos se cargan desde Admin → Materiales, con la regla de que sean
// reales, no genéricas — ver GestorMateriales.js).
// A partir del "modulo" (ej. "suelos_porcelánico") arma la etiqueta legible
// del sub-filtro ("Porcelánico") — genérico, no hace falta un diccionario
// nuevo cada vez que se agregue un submódulo a cualquier categoría.
const labelSubmodulo = (modulo, categoria) => {
  const resto = modulo.replace(`${categoria}_`, "");
  return resto
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const CatalogoMateriales = () => {
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriaActiva, setCategoriaActiva] = useState("todos");
  const [submoduloActivo, setSubmoduloActivo] = useState("todos");
  const [materialAbierto, setMaterialAbierto] = useState(null);
  const [error, setError] = useState(false);

  const cargarMateriales = () => {
    setLoading(true);
    setError(false);
    api.get("/materiales-publico")
      .then(setMateriales)
      .catch((e) => {
        console.error("Error cargando materiales:", e);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargarMateriales();
  }, []);

  const categoriasConItems = useMemo(() => {
    const presentes = new Set(materiales.map((m) => m.categoria));
    return Object.keys(CATEGORIAS_LABEL).filter((c) => presentes.has(c));
  }, [materiales]);

  // Submódulos reales de la categoría activa (ej. dentro de "suelos":
  // porcelánico/laminado/piedra natural/madera natural/vinílico) — solo
  // cuenta un "modulo" como submódulo si arranca con "categoria_", así los
  // materiales viejos (modulo="suelos" a secas) no generan un submódulo
  // fantasma, solo se ven en "Todos".
  const submodulosConItems = useMemo(() => {
    if (categoriaActiva === "todos") return [];
    const prefijo = `${categoriaActiva}_`;
    const modulos = new Set(
      materiales
        .filter((m) => m.categoria === categoriaActiva && (m.modulo || "").startsWith(prefijo))
        .map((m) => m.modulo)
    );
    return [...modulos].sort();
  }, [materiales, categoriaActiva]);

  const materialesFiltrados = useMemo(() => {
    let lista = materiales;
    if (categoriaActiva !== "todos") lista = lista.filter((m) => m.categoria === categoriaActiva);
    if (submoduloActivo !== "todos") lista = lista.filter((m) => m.modulo === submoduloActivo);
    return lista;
  }, [materiales, categoriaActiva, submoduloActivo]);

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="hero-header" style={{ padding: "24px 20px" }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="flex items-center gap-2 mb-2">
            <Package size={28} weight="fill" style={{ color: "var(--brand-accent)" }} />
          </div>
          <div className="text-h2 font-display">Materiales</div>
          <div className="text-small" style={{ opacity: 0.8, marginTop: 4 }}>
            Tipos, tonos y calidades — para que elijas el estilo antes de comprar
          </div>
        </div>
      </div>

      <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
        <div className="card p-3 mb-4" style={{ background: "color-mix(in srgb, var(--info) 8%, white)", border: "1px solid var(--info)" }}>
          <div className="text-tiny" style={{ color: "var(--info)" }}>
            ℹ️ Estas fotos son de referencia para ayudarte a elegir estilo, tono y calidad — no vendemos materiales, nosotros nos encargamos de la instalación. Compra algo similar a lo que elijas acá (vos, o lo coordinamos juntos) y lo instalamos.
          </div>
        </div>

        {categoriasConItems.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto" style={{ paddingBottom: 2 }}>
            <button
              onClick={() => { setCategoriaActiva("todos"); setSubmoduloActivo("todos"); }}
              style={{
                flexShrink: 0, border: "1.5px solid var(--border-subtle)", borderRadius: 999, padding: "8px 14px",
                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                background: categoriaActiva === "todos" ? "var(--brand-primary)" : "var(--surface-secondary)",
                color: categoriaActiva === "todos" ? "#fff" : "var(--text-primary)",
              }}
            >
              Todos
            </button>
            {categoriasConItems.map((c) => (
              <button
                key={c}
                onClick={() => { setCategoriaActiva(c); setSubmoduloActivo("todos"); }}
                style={{
                  flexShrink: 0, border: "1.5px solid var(--border-subtle)", borderRadius: 999, padding: "8px 14px",
                  fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                  background: categoriaActiva === c ? "var(--brand-primary)" : "var(--surface-secondary)",
                  color: categoriaActiva === c ? "#fff" : "var(--text-primary)",
                }}
              >
                {CATEGORIAS_LABEL[c] || c}
              </button>
            ))}
          </div>
        )}

        {submodulosConItems.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto" style={{ paddingBottom: 2 }}>
            <button
              onClick={() => setSubmoduloActivo("todos")}
              style={{
                flexShrink: 0, border: "1px solid var(--border-subtle)", borderRadius: 999, padding: "6px 12px",
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                background: submoduloActivo === "todos" ? "var(--brand-accent)" : "transparent",
                color: submoduloActivo === "todos" ? "#fff" : "var(--text-secondary)",
              }}
            >
              Todos
            </button>
            {submodulosConItems.map((sm) => (
              <button
                key={sm}
                onClick={() => setSubmoduloActivo(sm)}
                style={{
                  flexShrink: 0, border: "1px solid var(--border-subtle)", borderRadius: 999, padding: "6px 12px",
                  fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                  background: submoduloActivo === sm ? "var(--brand-accent)" : "transparent",
                  color: submoduloActivo === sm ? "#fff" : "var(--text-secondary)",
                }}
              >
                {labelSubmodulo(sm, categoriaActiva)}
              </button>
            ))}
          </div>
        )}

        {materialesFiltrados.length === 0 ? (
          error ? (
            <EmptyState
              icon="⚠️"
              title="No se pudo cargar el catálogo"
              description="Revisá tu conexión e intentá de nuevo"
              action={<Button variant="outline" onClick={cargarMateriales}>Reintentar</Button>}
            />
          ) : (
            <EmptyState
              icon="🧱"
              title="Todavía no hay fotos de materiales"
              description="Estamos cargando referencias reales de suelos, cerámica y más — volvé pronto."
            />
          )
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {materialesFiltrados.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-0 overflow-hidden"
                style={{ cursor: "pointer" }}
                onClick={() => setMaterialAbierto(m)}
              >
                <img
                  src={m.foto_url}
                  alt={m.nombre}
                  loading="lazy"
                  style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                />
                <div className="p-3">
                  <div className="font-bold text-tiny">{m.nombre}</div>
                  <div className="text-tiny text-secondary">{m.color || CATEGORIAS_LABEL[m.categoria] || m.categoria}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {materialAbierto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMaterialAbierto(null)}
            style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "flex-end" }}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              style={{ background: "var(--bg-base)", width: "100%", maxHeight: "92vh", overflow: "auto", borderRadius: "20px 20px 0 0" }}
            >
              <div style={{ position: "relative" }}>
                <img
                  src={materialAbierto.foto_url}
                  alt={materialAbierto.nombre}
                  style={{ width: "100%", maxHeight: "55vh", objectFit: "cover", display: "block" }}
                />
                <button
                  onClick={() => setMaterialAbierto(null)}
                  className="icon-button"
                  style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "50%" }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                <div className="text-tiny font-bold" style={{ color: "var(--brand-accent)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {CATEGORIAS_LABEL[materialAbierto.categoria] || materialAbierto.categoria}
                </div>
                <div className="text-h3 font-display mt-1">{materialAbierto.nombre}</div>
                {(materialAbierto.color || materialAbierto.marca) && (
                  <div className="text-small text-secondary mt-1">
                    {[materialAbierto.color, materialAbierto.marca].filter(Boolean).join(" · ")}
                  </div>
                )}
                {materialAbierto.descripcion && (
                  <div className="text-small text-secondary mt-2">{materialAbierto.descripcion}</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
